/**
 * i18n bootstrap. Initializes i18next + react-i18next with English and Arabic
 * resources, persists the choice to localStorage, and keeps the document's
 * `dir`/`lang` in sync so the whole UI flips to RTL for Arabic.
 *
 * RTL relies on Tailwind's logical utilities (ps-/pe-/ms-/me-/start-/end-/
 * text-start/end/rounded-s/border-s), which flip automatically under
 * `dir="rtl"`. Physical utilities (pl-/pr-/ml-/mr-/text-left/right) do NOT flip
 * and are being migrated to logical ones.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import en from './locales/en.json';
import ar from './locales/ar.json';

export const SUPPORTED_LANGUAGES = ['en', 'ar'] as const;
export type AppLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/** Languages that render right-to-left. */
const RTL_LANGUAGES = new Set<string>(['ar']);

export const isRtlLanguage = (lng?: string): boolean =>
  RTL_LANGUAGES.has((lng ?? '').split('-')[0]);

/** Set `<html dir>` + `<html lang>` from the active language. */
export const applyDocumentDirection = (lng?: string): void => {
  if (typeof document === 'undefined') return;
  const base = (lng ?? 'en').split('-')[0];
  const el = document.documentElement;
  el.setAttribute('dir', isRtlLanguage(base) ? 'rtl' : 'ltr');
  el.setAttribute('lang', base);
};

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      ar: { translation: ar },
    },
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    nonExplicitSupportedLngs: true, // treat `ar-SA` etc. as `ar`
    interpolation: { escapeValue: false }, // React already escapes
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'ideoz_lang',
      caches: ['localStorage'],
    },
  });

// Apply direction on boot and whenever the language changes.
applyDocumentDirection(i18n.resolvedLanguage || i18n.language);
i18n.on('languageChanged', applyDocumentDirection);

export default i18n;
